/**
 * LiveFeed — real + simulated pull activity feed
 *
 * Real pulls: when a user opens a pack the CardPacksPage calls `pushRealPull()`
 * on this module's singleton. The feed always shows those first.
 *
 * Simulated pulls: fill the rest of the list so it looks active even before
 * any wallet is connected. Swapped out for real data as the platform grows.
 */

import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GRADED_POOL } from "../hooks/usePokemonTCG";
import type { LiveCard } from "../hooks/usePokemonTCG";
import { TIER_DATA } from "./PackSelector";
import { DEMO_MODE } from "../demoMode";
import "./LiveFeed.scss";

// ── Card image helper — use high-res front face URL ───────────────────────────
// pokemontcg.io CDN: /sets/<setId>/<number>.png is ALWAYS the card front.
// We prefer imageLarge so the 40×56 thumbnail still looks crisp.
function cardFrontUrl(card: LiveCard): string {
  return card.imageLarge ?? card.imageSmall;
}

// ── Singleton real-pull bus ────────────────────────────────────────────────────
export interface FeedEntry {
  id:      number;
  wallet:  string;
  card:    LiveCard;
  tier:    typeof TIER_DATA[number];
  ts:      number; // epoch ms
  isReal:  boolean;
}

let _nextId = Date.now(); // seeded so HMR reloads don't collide with preserved state
const _realPulls: FeedEntry[] = [];
const _listeners: Set<() => void> = new Set();

/** Called by CardPacksPage when a user opens a real pack. */
export function pushRealPull(wallet: string, card: LiveCard, tierIdx: number) {
  const entry: FeedEntry = {
    id:     _nextId++,
    wallet: wallet.slice(0, 6) + "…" + wallet.slice(-4),
    card,
    tier:   TIER_DATA[Math.min(tierIdx, TIER_DATA.length - 1)],
    ts:     Date.now(),
    isReal: true,
  };
  _realPulls.unshift(entry);
  if (_realPulls.length > 50) _realPulls.pop();
  _listeners.forEach((fn) => fn());
}

// ── Simulated pull generator ──────────────────────────────────────────────────
// Use ALL graded pool cards (they have reliable images + known values)
const SIM_POOL = [...GRADED_POOL].sort((a, b) => b.approxValueUsd - a.approxValueUsd);

const SIM_WALLETS = [
  "0x71C7…3fA8","0xbEef…12Ac","0x3a9B…77dF","0xFa42…C831",
  "0x10Cd…4B2e","0x98AB…00C1","0xDead…B33F","0x5511…aA22",
  "0xC0ff…eE01","0x2B4d…9F3c","0xAbBa…dE47","0x6699…11Bc",
];

function pickSimCard(): LiveCard {
  // Power-law weighting: index 0 (most expensive) ~10× less likely than index 5
  const idx = Math.floor(Math.pow(Math.random(), 1.6) * SIM_POOL.length);
  return SIM_POOL[Math.min(idx, SIM_POOL.length - 1)];
}

function makeSimEntry(): FeedEntry {
  const card   = pickSimCard();
  const wallet = SIM_WALLETS[Math.floor(Math.random() * SIM_WALLETS.length)];
  const tierIdx = card.approxValueUsd > 5000 ? 6
    : card.approxValueUsd > 2500 ? 5
    : card.approxValueUsd > 1000 ? 4
    : card.approxValueUsd > 500  ? 3
    : card.approxValueUsd > 100  ? 2
    : card.approxValueUsd > 30   ? 1
    : 0;
  return {
    id:     _nextId++,
    wallet,
    card,
    tier:   TIER_DATA[tierIdx],
    ts:     Date.now() - Math.floor(Math.random() * 300_000), // up to 5 min ago
    isReal: false,
  };
}

function makeInitialSim(count = 10): FeedEntry[] {
  return Array.from({ length: count }, makeSimEntry);
}

function formatAgo(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 5)   return "just now";
  if (s < 60)  return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

const RARITY_ICON: Record<string, string> = {
  graded: "🏆", ultra: "💎", holo: "✨", rare: "⭐", uncommon: "🔵", common: "⬜",
};

// ── Mobile ticker component (auto-scrolling strip) ────────────────────────────

export function LiveTicker() {
  const [entries, setEntries] = useState<FeedEntry[]>(() => makeInitialSim(16));

  useEffect(() => {
    const fn = () => {
      const sim = makeInitialSim(Math.max(0, 16 - _realPulls.length));
      setEntries([..._realPulls, ...sim].slice(0, 16));
    };
    _listeners.add(fn);
    return () => { _listeners.delete(fn); };
  }, []);

  if (entries.length === 0) return null;

  // Duplicate for seamless loop
  const doubled = [...entries, ...entries];

  return (
    <div className="LiveTicker" aria-hidden="true">
      <div className="LiveTicker__inner">
        {doubled.map((e, i) => (
          <span key={i} className={`LiveTicker__item${e.isReal ? " real" : ""}`}>
            <span className="LiveTicker__icon">{RARITY_ICON[e.card.rarity] ?? "🃏"}</span>
            <span className="LiveTicker__wallet">{e.wallet}</span>
            <span className="LiveTicker__card">{e.card.name}</span>
            <span className="LiveTicker__val" style={{ color: e.tier.accent }}>
              ${e.card.approxValueUsd >= 1000
                ? (e.card.approxValueUsd / 1000).toFixed(1) + "k"
                : e.card.approxValueUsd.toLocaleString()}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────
interface Props {
  maxEntries?: number;
  simInterval?: number; // ms between new simulated entries
}

export function LiveFeed({ maxEntries = 14, simInterval = 9000 }: Props) {
  const [simEntries, setSimEntries] = useState<FeedEntry[]>(makeInitialSim);
  const [realEntries, setRealEntries] = useState<FeedEntry[]>([]);
  const [, setTick] = useState(0);
  const simRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Subscribe to real pull events
  useEffect(() => {
    const fn = () => setRealEntries([..._realPulls]);
    _listeners.add(fn);
    return () => { _listeners.delete(fn); };
  }, []);

  // Tick "ago" display every 15 s
  useEffect(() => {
    tickRef.current = setInterval(() => setTick((t) => t + 1), 15_000);
    return () => clearInterval(tickRef.current!);
  }, []);

  // Add simulated entries periodically
  useEffect(() => {
    simRef.current = setInterval(() => {
      setSimEntries((prev) => [makeSimEntry(), ...prev].slice(0, maxEntries));
    }, simInterval);
    return () => clearInterval(simRef.current!);
  }, [simInterval, maxEntries]);

  // Merge real (always first) + sim entries
  const combined = [...realEntries, ...simEntries].slice(0, maxEntries);

  return (
    <div className="LiveFeed">
      <div className="LiveFeed__header">
        <span className="LiveFeed__live-dot" />
        <span className="LiveFeed__live-label">{DEMO_MODE ? "DEMO PULLS · SIMULATED" : "LIVE PULLS"}</span>
        <span className="LiveFeed__count">{combined.length} recent</span>
      </div>

      <div className="LiveFeed__scroll">
        <AnimatePresence initial={false}>
          {combined.map((entry) => (
            <motion.div
              key={entry.id}
              className={`LiveFeed__entry${entry.isReal ? " real" : ""}`}
              initial={{ opacity: 0, y: -18, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, height: 0, marginBottom: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              layout
            >
              {/* Card image — always front face */}
              <div
                className={`LiveFeed__thumb${entry.card.isGraded ? " is-graded" : ""}`}
                style={{ borderColor: entry.tier.accent + "80" }}
              >
                <img
                  src={cardFrontUrl(entry.card)}
                  alt={entry.card.name}
                  className="LiveFeed__thumb-img"
                  // Fallback: if image fails, keep the card-art background
                  onError={(e) => {
                    const img = e.currentTarget as HTMLImageElement;
                    if (entry.card.imageSmall && img.src !== entry.card.imageSmall) {
                      img.src = entry.card.imageSmall;
                    } else {
                      img.style.display = "none";
                    }
                  }}
                />
                {entry.card.isGraded && entry.card.grade != null && (
                  <div className="LiveFeed__slab-badge">
                    <span>{entry.card.gradingCompany}</span>
                    <strong>{entry.card.grade}</strong>
                  </div>
                )}
              </div>

              {/* Text */}
              <div className="LiveFeed__text">
                <div className="LiveFeed__row1">
                  <span className="LiveFeed__wallet">
                    {entry.isReal && <span className="LiveFeed__you-badge">YOU · </span>}
                    {entry.wallet}
                  </span>
                  <span className="LiveFeed__value" style={{ color: entry.tier.accent }}>
                    ${entry.card.approxValueUsd.toLocaleString()}
                  </span>
                </div>

                <div className="LiveFeed__row2">
                  <span className="LiveFeed__rarity-icon">{RARITY_ICON[entry.card.rarity] ?? "🃏"}</span>
                  <span className="LiveFeed__card-name">{entry.card.name}</span>
                </div>

                <div className="LiveFeed__row3">
                  <span className="LiveFeed__tier-tag" style={{ color: entry.tier.accent }}>
                    {entry.tier.name}
                  </span>
                  <span className="LiveFeed__ago">{formatAgo(entry.ts)}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}
